/**
 * Shared Operations Dashboard building blocks.
 *
 * Used by every vertical's Operations Dashboard page:
 *   KpiCard     — icon + big number + optional sub-label
 *   StepPill    — numbered step row with optional urgent badge, navigates on click
 *   ActionBanner — amber "X items require attention" strip
 */
import React from 'react';
import { Zap, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Card from './Card';

// ─── KpiCard ─────────────────────────────────────────────────────────────────

export interface KpiCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  colour: string;         // Tailwind bg + text classes for the icon pod
  onClick?: () => void;
}

export function KpiCard({ label, value, sub, icon: Icon, colour, onClick }: KpiCardProps) {
  return (
    <Card padding="sm" className={onClick ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}>
      <div className="flex items-start gap-3" onClick={onClick}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colour}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight mt-0.5">{value}</p>
          {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

// ─── StepPill ─────────────────────────────────────────────────────────────────

export interface StepPillProps {
  step: string;
  label: string;
  count?: number;
  urgent?: boolean;
  path: string;
}

export function StepPill({ step, label, count, urgent, path }: StepPillProps) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left w-full transition-all hover:shadow-sm ${
        urgent && count
          ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/10 hover:border-red-400'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      <span className={`text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${
        urgent && count
          ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
      }`}>
        {step}
      </span>
      <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200 text-left">{label}</span>
      {count != null && count > 0 && (
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
          urgent ? 'bg-red-500 text-white' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
        }`}>
          {count}
        </span>
      )}
      <ArrowRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
    </button>
  );
}

// ─── ActionBanner ─────────────────────────────────────────────────────────────

export interface ActionBannerProps {
  count: number;
}

export function ActionBanner({ count }: ActionBannerProps) {
  if (count === 0) return null;
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
      <Zap className="w-5 h-5 text-amber-600 flex-shrink-0" />
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
        <strong>{count}</strong> item{count !== 1 ? 's' : ''} require{count === 1 ? 's' : ''} your attention across the pipeline workflow
      </p>
    </div>
  );
}
